import { useState, useEffect } from "react";
import { Check } from "lucide-react";
import { groupService } from "../../../../services/admin/groupService.js";
import { screenService } from "../../../../services/admin/screenService.js";

export default function AddRoleForm({ editData, onBack }) {
  const [roleName, setRoleName] = useState("");
  const [defaultPage, setDefaultPage] = useState("");
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleNameError, setRoleNameError] = useState("");

  // ✅ Toast state
  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: "", type: "success" }), 1500);
  };

  /* LOAD SCREENS */
  useEffect(() => {
    const fetchScreens = async () => {
      try {
        const response = await screenService.getAll();
        if (response?.data) setScreens(response.data);
      } catch (error) {
        console.error("Error loading screens", error);
      }
    };
    fetchScreens();
  }, []);

  /* POPULATE FORM WHEN EDITING */
  useEffect(() => {
    if (editData) {
      setRoleName(editData.roleName || "");
      setDefaultPage(editData.screenName || "");
    }
  }, [editData]);

  /* SAVE OR UPDATE ROLE */
  const handleSubmit = async () => {
    if (!roleName.trim()) {
      setRoleNameError("Role Name is required");
      return;
    }
    if (!defaultPage) {
      showToast("Please select a default page", "error");
      return;
    }

    setLoading(true);

    try {
      const payload = { roleName, defaultPage };

      if (editData) {
        await groupService.update({ groupId: editData.groupId, ...payload });
        showToast("Role updated successfully", "success"); // ✅ update success
      } else {
        await groupService.create(payload);
        showToast("Role created successfully", "success"); // ✅ create success
      }

      setRoleName("");
      setDefaultPage("");

      // ✅ Let toast show before navigating back
      setTimeout(() => {
        if (onBack) onBack();
      }, 1600);

    } catch (error) {
      console.error("Error saving role", error);
      showToast("Something went wrong. Please try again.", "error"); // ✅ catch error
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="ad-card ad-card-lg ad-animate">
        {/* HEADER */}
        <div className="ad-card-header">
          <h3 className="ad-card-title">
            {editData ? "Edit Role" : "Add Role"}
          </h3>
        </div>

        <div className="ad-onboard-scroll">
          <div className="ad-grid-3">
            {/* ROLE NAME */}
            <div>
              <label className="ad-label">Role Name <span className="text-red-500">*</span></label>
              <input
                className={`ad-input${roleNameError ? ' border-red-400' : ''}`}
                value={roleName}
                onChange={(e) => { setRoleName(e.target.value); if (e.target.value.trim()) setRoleNameError(""); }}
                placeholder="Enter Role Name"
              />
              {roleNameError && <p className="text-red-500 text-xs mt-1">{roleNameError}</p>}
            </div>

            {/* DEFAULT PAGE */}
            <div>
              <label className="ad-label">Default Page</label>
              <div className="ad-select-wrap">
                <select
                  className="ad-select"
                  value={defaultPage}
                  onChange={(e) => setDefaultPage(e.target.value)}
                >
                  <option value="">Select Default Page</option>
                  {screens.map((screen) => (
                    <option key={screen.screenId} value={screen.screenName}>
                      {screen.screenName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div style={{ marginTop: "2rem", textAlign: "right", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <button type="button" className="ad-btn" onClick={onBack}>
              Back
            </button>
            <button
              type="button"
              className="ad-btn ad-btn-green"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Saving..." : editData ? "Update Role" : "Save Role"}
            </button>
          </div>
        </div>
      </div>

      {toast.visible && (
        <div className="fixed top-6 right-6 z-[9999] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
            toast.type === "success" ? "bg-[#36a852]" : "bg-red-500"
          }`}>
            {toast.type === "success" ? (
              <Check size={14} strokeWidth={3} />
            ) : (
              <span style={{ fontSize: "14px", fontWeight: "bold", lineHeight: 1 }}>✕</span>
            )}
          </div>
          <span className="text-sm font-semibold">{toast.message}</span>
        </div>
      )}
    </>
  );
}